import React, { useContext, useEffect, useState, useCallback } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import Form from 'react-bootstrap/Form'
import Col from 'react-bootstrap/Col'
import InputGroup from 'react-bootstrap/InputGroup'
import ListadoDomiciliosHooks from '../components/ForReactHooks/ListadoDomiciliosHooks'
import ListadoDomiciliosEstaticosHooks from '../components/ForReactHooks/ListadoDomiciliosEstaticosHooks'

import {
  requestTraerInfoActividadComercial,
  requestGuardarDatosComercial,
  emptyPromise
} from '../utils/requests'

import CargandoDatosLoader from '../components/CargandoDatosLoader'
import { StepContext } from '../StepContext'

const DatosComercial = (props) => {
  const schema = yup.object().shape({
    datosGenerales: yup.object().shape({
      cantidadEmpleados: yup.number().required().min(0).integer(),
      kwMonto: yup.number().required().min(0),
      alquilerDevengado: yup.number().required().min(0)
    }),
    datosExterno: yup.object().shape({
      domicilioComercial: yup.array().of(
        yup.object().shape({
          calle: yup.string().required(),
          numero: yup.number().required().min(0).integer(),
          piso: yup
            .number()
            .integer()
            .nullable()
            .transform((value, originalValue) =>
              String(originalValue).trim() === '' ? null : value
            ),
          codigoPostal: yup.string().required(),
          partidaInmobiliaria: yup.string().required(),
          localidad: yup.string().required(),
          habilitacion: yup.object().shape({
            habilitacion: yup.number().required(),
            actividadPrincipal: yup.number(),
            actividadSecundaria: yup.array().of(yup.number())
          })
        })
      ),
      importeTISH: yup
        .number()
        .min(0)
        .nullable()
        .transform((value, originalValue) =>
          String(originalValue).trim() === '' ? null : value
        ),
      fechaUltimo: yup
        .date()
        .nullable()
        .transform((value, originalValue) =>
          String(originalValue).trim() === '' ? null : value
        )
    })
  })
  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      datosGenerales: {
        cantidadEmpleados: null,
        kwMonto: null,
        alquilerDevengado: null
      },
      datosBerisso: {
        domicilioComercial: [
          {
            calle: '',
            numero: '',
            piso: '',
            codigoPostal: '',
            partidaInmobiliaria: '',
            legajo: {
              legajo: '',
              actividadPrincipal: 0,
              actividadSecundaria: []
            }
          }
        ]
      },
      datosExterno: {
        domicilioComercial: [
          {
            calle: '',
            numero: '',
            piso: '',
            codigoPostal: '',
            partidaInmobiliaria: '',
            legajo: {
              legajo: '',
              actividadPrincipal: 0,
              actividadSecundaria: []
            }
          }
        ],
        importeTISH: '',
        fechaUltimo: ''
      }
    }
  })

  const { register, errors, trigger, control, formState, getValues, reset } =
    methods

  const {
    fields: domiciliosExternos,
    append: agregarDomicilioExterno,
    remove: quitarDomicilioExterno
  } = useFieldArray({
    control,
    name: 'datosExterno.domicilioComercial'
  })

  const { fields: domiciliosBerisso } = useFieldArray({
    control,
    name: 'datosBerisso.domicilioComercial'
  })

  const { setPuedeAvanzar, setCallback } = useContext(StepContext)
  const [actividades, setActividades] = useState([])
  const [actividadesiibb, setActividadesIIBB] = useState([])
  const [loading, setLoading] = useState(true)

  const guardarDatos = useCallback(() => {
    trigger()
    if (!formState.isValid) {
      return emptyPromise()
    }
    const datos = JSON.stringify(getValues({ nest: true }))
    return requestGuardarDatosComercial(datos).then(({ status, content }) => {
      if (status === 200) {
        return true
      } else {
        trigger()
        console.log(content)
      }
    })
  }, [formState.isValid, getValues, trigger])

  useEffect(() => {
    setLoading(true)
    requestTraerInfoActividadComercial()
      .then(({ status, content }) => {
        if (status === 200) {
          setActividades(content.actividadesComerciales)
          setActividadesIIBB(content.actividadesiibb)
          reset({
            datosGenerales: content.datosGenerales,
            datosExterno: content.datosExternos,
            datosBerisso: content.datosComerciales
          })
        }
      })
      .finally(() => setLoading(false))
  }, [reset])

  useEffect(() => {
    setCallback(guardarDatos)
    setPuedeAvanzar(true)
  }, [guardarDatos, setPuedeAvanzar, setCallback])

  return (
    <div className="container">
      <h6 className="text-muted text-normal text-uppercase">
        Datos de la Actividad Comercial
      </h6>
      <hr className="margin-bottom-1x" />

      <CargandoDatosLoader cargando={loading}>
        <Form onSubmit={(e) => e.preventDefault()}>
          <Form.Row>
            <Col>
              <Form.Group controlId="formCantEmpleados">
                <Form.Label>Cantidad de Empleados*</Form.Label>
                <Form.Control
                  type="number"
                  isInvalid={!!errors.datosGenerales?.cantidadEmpleados}
                  isValid={
                    !errors.datosGenerales?.cantidadEmpleados &&
                    getValues('datosGenerales.cantidadEmpleados')
                  }
                  ref={register()}
                  name="datosGenerales.cantidadEmpleados"
                  placeholder="Ingrese cantidad de empleados"
                />
                <Form.Control.Feedback type="invalid">
                  {!!errors.datosGenerales?.cantidadEmpleados &&
                    'La cantidad debe ser mayor o igual a cero y no admite decimales.'}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formAlquiler">
                <Form.Label>Alquiler devengado en valor monetario</Form.Label>
                <InputGroup>
                  <InputGroup.Prepend
                    className="input-group-addon"
                    style={{ top: '25px' }}
                  >
                    <InputGroup.Text id="prependAlquilerDevengado">
                      $
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    type="number"
                    isInvalid={!!errors.datosGenerales?.alquilerDevengado}
                    isValid={
                      !errors.datosGenerales?.alquilerDevengado &&
                      getValues('datosGenerales.alquilerDevengado')
                    }
                    ref={register()}
                    placeholder="Ingrese monto en pesos"
                    aria-describedby="prependAlquilerDevengado"
                    name={'datosGenerales.alquilerDevengado'}
                  />
                  <Form.Control.Feedback type="invalid">
                    {!!errors.datosGenerales?.alquilerDevengado &&
                      'La cantidad debe ser mayor o igual a cero.'}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Form.Row>
          <Form.Group controlId="formEnergia">
            <Form.Row>
              <Col>
                <Form.Label>CONSUMO ANUAL DE ENERGÍA ELÉCTRICA - $ </Form.Label>
                <InputGroup>
                  <InputGroup.Prepend
                    className="input-group-addon"
                    style={{ top: '25px' }}
                  >
                    <InputGroup.Text id="prependConsumoAnual">
                      $
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    type="number"
                    isInvalid={!!errors.datosGenerales?.kwMonto}
                    isValid={
                      !errors.datosGenerales?.kwMonto &&
                      getValues('datosGenerales.kwMonto')
                    }
                    ref={register()}
                    placeholder="Ingrese Monto en pesos ($)"
                    aria-describedby="prependConsumoAnual"
                    name={'datosGenerales.kwMonto'}
                  />
                  <Form.Control.Feedback type="invalid">
                    {!!errors.datosGenerales?.kwMonto &&
                      'La cantidad debe ser mayor o igual a cero.'}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Form.Row>
          </Form.Group>

          <h6 className="text-muted text-normal text-uppercase">
            Datos de la Actividad Comercial en Berisso
          </h6>
          <hr className="margin-bottom-1x" />
          <Form.Group controlId="formDomicilios">
            <ListadoDomiciliosEstaticosHooks
              actividades={actividades}
              actividadesiibb={actividadesiibb}
              isDisabled={true}
              donde={'datosBerisso'}
              errors={errors}
              listado={domiciliosBerisso}
              append={() => {}}
              remove={() => {}}
              register={register}
              control={control}
              nombre={'datosBerisso.domicilioComercial'}
            />
          </Form.Group>
          <h6 className="text-muted text-normal text-uppercase">
            Datos de la Actividad Comercial en Otras Localidades de la Provincia
            de Bs. As.
          </h6>
          <hr className="margin-bottom-1x" />
          <Form.Group controlId="formDomiciliosExternos">
            <ListadoDomiciliosHooks
              actividades={actividades}
              actividadesiibb={actividadesiibb}
              isDisabled={false}
              donde={'datosExterno'}
              errors={errors}
              listado={domiciliosExternos}
              append={agregarDomicilioExterno}
              remove={quitarDomicilioExterno}
              getValues={(e) => getValues(e)}
              register={register}
              control={control}
              nombre={'datosExterno.domicilioComercial'}
            />
            <Form.Control.Feedback type="invalid">
              Hay un error en algun campo
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Row>
            <Col>
              <Form.Group controlId="formBasicEmail">
                <Form.Label>
                  Importe anual abonado en concepto de TISH
                </Form.Label>
                <InputGroup>
                  <InputGroup.Prepend
                    className="input-group-addon"
                    style={{ top: '25px' }}
                  >
                    <InputGroup.Text id="prependabonadoAnualTish">
                      $
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    type="number"
                    placeholder="Ingrese Importe"
                    aria-describedby="prependabonadoAnualTish"
                    isInvalid={!!errors?.datosExterno?.importeTISH}
                    isValid={
                      !errors?.datosExterno?.importeTISH &&
                      getValues(`datosExterno.importeTISH`)
                    }
                    ref={register({ required: domiciliosExternos.length > 0 })}
                    name={'datosExterno.importeTISH'}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formBasicEmail">
                <Form.Label>Último TISH</Form.Label>
                <Form.Control
                  type="date"
                  isInvalid={!!errors?.datosExterno?.fechaUltimo}
                  isValid={
                    !errors?.datosExterno?.fechaUltimo &&
                    getValues(`datosExterno.fechaUltimo`)
                  }
                  placeholder="Seleccione Fecha"
                  ref={register({ required: domiciliosExternos.length > 0 })}
                  name={'datosExterno.fechaUltimo'}
                />
              </Form.Group>
            </Col>
          </Form.Row>
        </Form>
        {/*<div>*/}
        {/*<a onClick={() => console.log(getValues({nest:true}))}>Valores</a>*/}
        {/*<a onClick={() => console.log('formState',formState)}>FormState</a>*/}
        {/*<a onClick={() => console.log('errors',errors)}>Errores</a>*/}
        {/*<a onClick={() => trigger()}>Trigger</a>*/}
        {/*</div>*/}
      </CargandoDatosLoader>
    </div>
  )
}

export default DatosComercial
